<template>
  <section class="machinery-section">
    <div class="container">
      <!-- Başlık -->
      <div class="section-header text-center">
        <h2 class="section-title">{{ locale === 'tr' ? 'Makina Parkurumuz' : 'Our Machinery Fleet' }}</h2>
        <p class="section-subtitle">
          {{ locale === 'tr' ? 'Modern ve güvenilir Epiroc iş makinalarımız ile projelerinizi hayata geçiriyoruz' : 'Bringing your projects to life with our modern and reliable Epiroc machinery' }}
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="loader"></div>
        <p>{{ locale === 'tr' ? 'Makinalar yükleniyor...' : 'Loading machinery...' }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="error-icon">⚠️</div>
        <h3>{{ locale === 'tr' ? 'Veri Yüklenemedi' : 'Failed to Load Data' }}</h3>
        <p>{{ error }}</p>
        <button @click="loadMachinery" class="retry-btn">
          {{ locale === 'tr' ? 'Tekrar Dene' : 'Retry' }}
        </button>
      </div>

      <!-- Machinery Grid -->
      <div v-if="!loading && !error && machinery.length > 0" class="machinery-grid">
        <div 
          v-for="machine in machinery" 
          :key="machine.id"
          class="machinery-card"
          @click="handleMachineryClick(machine.id)"
          @mouseenter="handleMouseEnter(machine)"
          @mouseleave="handleMouseLeave(machine)"
        >
          <!-- Machine Image Slider -->
          <div class="machine-image-container">
            <div class="image-slider">
              <div 
                v-for="(image, index) in getMachineImages(machine)" 
                :key="index"
                class="slide"
                :class="{ 'active': machine.currentImageIndex === index }"
              >
                <img 
                  :src="image" 
                  :alt="`${locale === 'tr' ? machine.name_tr : machine.name_en} - ${index + 1}`"
                  class="machine-image"
                  @error="handleImageError"
                  loading="lazy"
                >
              </div>
            </div>
            
            <!-- Slider Controls -->
            <div v-if="getMachineImages(machine).length > 1" class="slider-controls">
              <button 
                @click="previousImage(machine)"
                class="slider-btn prev-btn"
                :disabled="machine.currentImageIndex === 0"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              <button 
                @click="nextImage(machine)"
                class="slider-btn next-btn"
                :disabled="machine.currentImageIndex === getMachineImages(machine).length - 1"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
            
            <!-- Slider Indicators -->
            <div v-if="getMachineImages(machine).length > 1" class="slider-indicators">
              <span 
                v-for="(image, index) in getMachineImages(machine)" 
                :key="index"
                class="indicator"
                :class="{ 'active': machine.currentImageIndex === index }"
                @click="setImage(machine, index)"
              ></span>
            </div>
            
            <div class="image-overlay">
              <div class="overlay-content">
                <span>{{ locale === 'tr' ? 'Detayları Görüntüle' : 'View Details' }}</span>
              </div>
            </div>
          </div>

          <!-- Machine Info -->
          <div class="machine-info">
            <div class="machine-brand">{{ machine.brand || 'EPIROC' }}</div>
            <h3 class="machine-model">{{ locale === 'tr' ? machine.name_tr : machine.name_en }}</h3>
            <p class="machine-type">{{ locale === 'tr' ? machine.category_tr : machine.category_en }}</p>
            
            <!-- Description -->
            <p class="machine-description">{{ locale === 'tr' ? machine.description_tr : machine.description_en }}</p>
            
            <!-- Specifications -->
            <div class="machine-specs" v-if="hasSpecifications(machine)">
              <h4>{{ locale === 'tr' ? 'Teknik Özellikler' : 'Specifications' }}</h4>
              <div class="spec-grid">
                <!-- Drilling Diameter -->
                <div v-if="machine.drilling_diameter" class="spec-item">
                  <span class="spec-label">{{ locale === 'tr' ? 'Delme Çapı' : 'Drilling Diameter' }}:</span>
                  <span class="spec-value">{{ machine.drilling_diameter }}</span>
                </div>
                <!-- Drilling Depth -->
                <div v-if="machine.drilling_depth" class="spec-item">
                  <span class="spec-label">{{ locale === 'tr' ? 'Delme Derinliği' : 'Drilling Depth' }}:</span>
                  <span class="spec-value">{{ machine.drilling_depth }}</span>
                </div>
                <!-- Motor Power -->
                <div v-if="machine.motor_power" class="spec-item">
                  <span class="spec-label">{{ locale === 'tr' ? 'Motor Gücü' : 'Motor Power' }}:</span>
                  <span class="spec-value">{{ machine.motor_power }}</span>
                </div>
                <!-- Weight -->
                <div v-if="machine.weight" class="spec-item">
                  <span class="spec-label">{{ locale === 'tr' ? 'Ağırlık' : 'Weight' }}:</span>
                  <span class="spec-value">{{ machine.weight }}</span>
                </div>
                <!-- Additional specifications from JSON -->
                <div 
                  v-for="(value, key) in getAdditionalSpecs(machine)" 
                  :key="key" 
                  class="spec-item"
                >
                  <span class="spec-label">{{ formatSpecLabel(key) }}:</span>
                  <span class="spec-value">{{ value }}</span>
                </div>
              </div>
            </div>

            <!-- Features -->
            <div class="machine-features" v-if="getMachineFeatures(machine).length > 0">
              <h4>{{ locale === 'tr' ? 'Özellikler' : 'Features' }}</h4>
              <ul>
                <li v-for="(feature, index) in getMachineFeatures(machine)" :key="index">
                  {{ feature }}
                </li>
              </ul>
            </div>

          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
import ApiService from '../services/api.js'

export default {
  name: 'MachinerySection',
  
  props: {
    locale: {
      type: String,
      default: 'tr'
    }
  },
  
  data() {
    return {
      machinery: [],
      isLoading: true,
      error: null
    }
  },

  async mounted() {
    await this.loadMachinery()
  },

  beforeUnmount() {
    // Auto-play interval'larını temizle
    if (this.machinery && this.machinery.length > 0) {
      this.machinery.forEach(machine => {
        this.stopAutoPlay(machine)
      })
    }
  },

  watch: {
    locale() {
      // Dil değiştiğinde veriyi yeniden yükle
      this.loadMachinery()
    }
  },

  methods: {
    handleMachineryClick(machineId) {
      // Yeni sayfaya yönlendir
      window.location.href = `?page=machinery&id=${machineId}`
    },
    
    async loadMachinery() {
      this.isLoading = true
      this.error = null

      try {
        const response = await ApiService.getMachinery({ limit: 6 })
        
        if (response && response.length > 0) {
          // API'den gelen veriyi işle
          this.machinery = response.map(machine => ({
            ...machine,
            currentImageIndex: 0, // Slider için başlangıç indexi
            autoPlayInterval: null, // Auto-play interval referansı
            // JSON alanları zaten API'de parse edilmiş olmalı
            specifications: machine.specifications || {},
            features_tr: machine.features_tr || [],
            features_en: machine.features_en || [],
            // Backward compatibility için features alanını ayarla
            features: this.locale === 'tr' ? (machine.features_tr || machine.features || []) : (machine.features_en || machine.features || [])
          }))
          
          // Auto-play'i başlat
          this.$nextTick(() => {
            this.machinery.forEach(machine => {
              this.startAutoPlay(machine)
            })
          })
        } else {
          throw new Error(this.locale === 'tr' ? 'Makina verisi bulunamadı' : 'No machinery data found')
        }

      } catch (error) {
        // console.error('Machinery loading error:', error)
        this.error = error.message || (this.locale === 'tr' ? 'Veri yüklenirken hata oluştu' : 'Error loading data')
        
        // Fallback data
        this.machinery = this.getFallbackMachinery()
        
        // Fallback makinalar için auto-play'i başlat
        this.$nextTick(() => {
          this.machinery.forEach(machine => {
            this.startAutoPlay(machine)
          })
        })
      } finally {
        this.isLoading = false
      }
    },

    parseJSON(jsonString) {
      try {
        return typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString
      } catch (e) {
        // console.warn('JSON parse error:', e)
        return jsonString || []
      }
    },

    formatSpecLabel(key) {
      const labelMap = {
        'drilling_diameter': this.locale === 'tr' ? 'Delme Çapı' : 'Drilling Diameter',
        'drilling_depth': this.locale === 'tr' ? 'Delme Derinliği' : 'Drilling Depth', 
        'motor_power': this.locale === 'tr' ? 'Motor Gücü' : 'Engine Power',
        'weight': this.locale === 'tr' ? 'Ağırlık' : 'Weight',
        'fuel_capacity': this.locale === 'tr' ? 'Yakıt Kapasitesi' : 'Fuel Capacity',
        'hydraulic_pressure': this.locale === 'tr' ? 'Hidrolik Basınç' : 'Hydraulic Pressure'
      }
      
      return labelMap[key] || key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    generateFallbackImage(machineName) {
      const encodedName = encodeURIComponent(machineName || 'EPIROC')
      return `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="250" viewBox="0 0 400 250"><rect width="400" height="250" fill="%23f8f9fa"/><rect x="20" y="20" width="360" height="210" fill="%23e9ecef" rx="10"/><text x="200" y="110" text-anchor="middle" fill="%236c757d" font-size="16" font-weight="bold">EPIROC</text><text x="200" y="135" text-anchor="middle" fill="%23adb5bd" font-size="14">${encodedName}</text><text x="200" y="155" text-anchor="middle" fill="%23ced4da" font-size="10">Görsel Yükleniyor...</text></svg>`
    },

    handleImageError(event) {
      const machineName = event.target.alt || 'EPIROC Makina'
      event.target.src = this.generateFallbackImage(machineName)
    },

    // Makine özelliklerini dil bazlı getir
    getMachineFeatures(machine) {
      if (this.locale === 'tr') {
        return machine.features_tr || machine.features || []
      } else {
        return machine.features_en || machine.features || []
      }
    },

    // Image slider methods
    getMachineImages(machine) {
      // API'den gelen görselleri veya fallback görseleri döndür
      if (machine.images && machine.images.length > 0) {
        return machine.images
      }
      
      // Test için örnek görseller
      const testImages = [
        machine.image_url || this.generateFallbackImage(machine.name_tr),
        'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
      ]
      
      return testImages
    },

    previousImage(machine) {
      if (machine.currentImageIndex > 0) {
        machine.currentImageIndex--
      }
    },

    nextImage(machine) {
      const maxIndex = this.getMachineImages(machine).length - 1
      if (machine.currentImageIndex < maxIndex) {
        machine.currentImageIndex++
      }
    },

    setImage(machine, index) {
      machine.currentImageIndex = index
    },

    // Auto-play functionality
    startAutoPlay(machine) {
      if (machine.autoPlayInterval) {
        clearInterval(machine.autoPlayInterval)
      }
      
      machine.autoPlayInterval = setInterval(() => {
        const images = this.getMachineImages(machine)
        if (images.length > 1) {
          machine.currentImageIndex = (machine.currentImageIndex + 1) % images.length
        }
      }, 5000) // 5 saniye
    },

    stopAutoPlay(machine) {
      if (machine.autoPlayInterval) {
        clearInterval(machine.autoPlayInterval)
        machine.autoPlayInterval = null
      }
    },

    handleMouseEnter(machine) {
      this.stopAutoPlay(machine)
    },

    handleMouseLeave(machine) {
      this.startAutoPlay(machine)
    },

    // Image slider methods
    getMachineImages(machine) {
      // API'den gelen görselleri veya fallback görselleri döndür
      if (machine.images && machine.images.length > 0) {
        return machine.images
      }
      
      // Test için örnek görseller
      const testImages = [
        machine.image_url || this.generateFallbackImage(machine.name_tr),
        'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
      ]
      
      return testImages
    },

    previousImage(machine) {
      if (machine.currentImageIndex > 0) {
        machine.currentImageIndex--
      }
    },

    nextImage(machine) {
      const maxIndex = this.getMachineImages(machine).length - 1
      if (machine.currentImageIndex < maxIndex) {
        machine.currentImageIndex++
      }
    },

    setImage(machine, index) {
      machine.currentImageIndex = index
    },

    // Teknik özellik varlığını kontrol et
    hasSpecifications(machine) {
      return machine.drilling_diameter || machine.drilling_depth || 
             machine.motor_power || machine.weight || 
             (machine.specifications && Object.keys(machine.specifications).length > 0)
    },

    // Ana alanlar dışındaki ek teknik özellikleri getir
    getAdditionalSpecs(machine) {
      if (!machine.specifications || typeof machine.specifications !== 'object') {
        return {}
      }
      
      const mainFields = ['drilling_diameter', 'drilling_depth', 'motor_power', 'weight']
      const additionalSpecs = {}
      
      Object.keys(machine.specifications).forEach(key => {
        if (!mainFields.includes(key)) {
          additionalSpecs[key] = machine.specifications[key]
        }
      })
      
      return additionalSpecs
    },

    getFallbackMachinery() {
      return [
        {
          id: 1,
          currentImageIndex: 0,
          autoPlayInterval: null,
          name_tr: 'FlexiROC T40',
          name_en: 'FlexiROC T40',
          category_tr: 'Yüzey Delme Makinası',
          category_en: 'Surface Drill Rig',
          description_tr: 'Kompakt ve güçlü yüzey delme makinası',
          description_en: 'Compact and powerful surface drilling machine',
          image_url: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          specifications: {
            'drilling_diameter': '76-127 mm',
            'drilling_depth': '25 m',
            'motor_power': '129 kW',
            'weight': '18,500 kg'
          },
          features: [
            'Otomatik rod değişimi',
            'GPS navigasyon sistemi',
            'Toz bastırma sistemi',
            'Çevre dostu motor teknolojisi'
          ]
        },
        {
          id: 2,
          currentImageIndex: 0,
          autoPlayInterval: null,
          name_tr: 'FlexiROC T35',
          name_en: 'FlexiROC T35',
          category_tr: 'Kompakt Delme Makinası',
          category_en: 'Compact Drill Rig',
          description_tr: 'Esnek ve verimli kompakt delme çözümü',
          description_en: 'Flexible and efficient compact drilling solution',
          image_url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          specifications: {
            'drilling_diameter': '64-115 mm',
            'drilling_depth': '20 m', 
            'motor_power': '105 kW',
            'weight': '15,200 kg'
          },
          features: [
            'Kompakt tasarım',
            'Kolay taşınabilirlik',
            'Düşük yakıt tüketimi',
            'Hızlı kurulum'
          ]
        },
        {
          id: 3,
          currentImageIndex: 0,
          autoPlayInterval: null,
          name_tr: 'PowerROC T25',
          name_en: 'PowerROC T25',
          category_tr: 'Kompakt Delme Makinası',
          category_en: 'Compact Drilling Machine',
          description_tr: 'Güçlü ve ekonomik delme çözümü',
          description_en: 'Powerful and economical drilling solution',
          image_url: 'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          specifications: {
            'drilling_diameter': '64-102 mm',
            'drilling_depth': '18 m', 
            'motor_power': '85 kW',
            'weight': '12,800 kg'
          },
          features: [
            'Ekonomik işletme',
            'Yüksek mobilite',
            'Kolay bakım',
            'Güvenilir performans'
          ]
        },
        {
          id: 4,
          currentImageIndex: 0,
          autoPlayInterval: null,
          name_tr: 'SmartROC T35',
          name_en: 'SmartROC T35',
          category_tr: 'Akıllı Delme İstasyonu',
          category_en: 'Smart Drilling Station',
          description_tr: 'Akıllı teknoloji ile donatılmış delme makinası',
          description_en: 'Drilling machine equipped with smart technology',
          image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          specifications: {
            'drilling_diameter': '76-127 mm',
            'drilling_depth': '30 m',
            'motor_power': '145 kW',
            'weight': '22,300 kg'
          },
          features: [
            'Otomatik delme sistemi',
            'Uzaktan kontrol',
            'Veri analizi',
            'Predictive maintenance'
          ]
        },
        {
          id: 5,
          currentImageIndex: 0,
          autoPlayInterval: null,
          name_tr: 'FlexiROC T45',
          name_en: 'FlexiROC T45',
          category_tr: 'Ağır Hizmet Delme Makinası',
          category_en: 'Heavy Duty Drilling Machine',
          description_tr: 'Ağır işler için tasarlanmış güçlü delme makinası',
          description_en: 'Powerful drilling machine designed for heavy operations',
          image_url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          specifications: {
            'drilling_diameter': '89-152 mm',
            'drilling_depth': '35 m',
            'motor_power': '175 kW',
            'weight': '26,500 kg'
          },
          features: [
            'Yüksek delme kapasitesi',
            'Güçlü motor sistemi',
            'Dayanıklı yapı',
            'Zorlu koşullara uygun'
          ]
        },
        {
          id: 6,
          currentImageIndex: 0,
          autoPlayInterval: null,
          name_tr: 'SmartROC T40',
          name_en: 'SmartROC T40',
          category_tr: 'İleri Teknoloji Delme Sistemi',
          category_en: 'Advanced Drilling System',
          description_tr: 'Gelişmiş otomasyon ve akıllı sistemler',
          description_en: 'Advanced automation and smart systems',
          image_url: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          specifications: {
            'drilling_diameter': '76-140 mm',
            'drilling_depth': '32 m',
            'motor_power': '160 kW',
            'weight': '24,200 kg'
          },
          features: [
            'Tam otomatik operasyon',
            'AI destekli optimizasyon',
            'Real-time monitoring',
            'Çevre dostu teknoloji'
          ]
        }
      ]
    }
  }
}
</script>

<style scoped>
.machinery-section {
  padding: 60px 0;
  background: linear-gradient(135deg, #f1f3f4 0%, #e8eaf6 100%);
  position: relative;
  overflow: hidden;
}

.machinery-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,30 1000,100 0,70"/></svg>');
  pointer-events: none;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
}

/* Section Header */
.section-header {
  margin-bottom: 50px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a365d;
  margin-bottom: 15px;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #ff6b35, #f7931e);
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #4a5568;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
}

/* Machinery Grid */
.machinery-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
}

.machinery-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  cursor: pointer;
}

.machinery-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
}



/* Machine Image */
.machine-image-container {
  position: relative;
  height: 220px;
  overflow: hidden;
}

/* Image Slider */
.image-slider {
  position: relative;
  width: 100%;
  height: 100%;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.slide.active {
  opacity: 1;
}

.machine-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.machinery-card:hover .machine-image {
  transform: scale(1.1);
}

/* Slider Controls */
.slider-controls {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.machinery-card:hover .slider-controls {
  opacity: 1;
  pointer-events: auto;
}

.slider-btn {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.slider-btn:hover {
  background: white;
  transform: scale(1.1);
}

.slider-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.slider-btn i {
  color: #1a365d;
  font-size: 12px;
}

/* Slider Indicators */
.slider-indicators {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background: white;
  transform: scale(1.2);
}

.indicator:hover {
  background: rgba(255, 255, 255, 0.8);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 54, 93, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.machinery-card:hover .image-overlay {
  opacity: 1;
}

.overlay-content {
  text-align: center;
  color: white;
}

.overlay-content i {
  font-size: 2rem;
  margin-bottom: 8px;
  display: block;
}

.overlay-content span {
  font-size: 0.9rem;
  font-weight: 500;
}

/* Machine Info */
.machine-info {
  padding: 25px;
}

.machine-brand {
  background: linear-gradient(45deg, #ff6b35, #f7931e);
  color: white;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  display: inline-block;
  margin-bottom: 10px;
}

.machine-model {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a365d;
  margin-bottom: 5px;
}

.machine-type {
  color: #4a5568;
  font-size: 0.95rem;
  margin-bottom: 20px;
  font-style: italic;
}

/* Specifications */
.machine-specs {
  margin-bottom: 20px;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
}

.spec-item:last-child {
  border-bottom: none;
}

.spec-label {
  font-weight: 500;
  color: #4a5568;
  font-size: 0.9rem;
}

.spec-value {
  font-weight: 600;
  color: #1a365d;
  font-size: 0.9rem;
}

/* Features */
.machine-features {
  margin-bottom: 20px;
}

.machine-features h4 {
  font-size: 1rem;
  color: #1a365d;
  margin-bottom: 10px;
  font-weight: 600;
}

.machine-features ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.machine-features li {
  padding: 4px 0;
  font-size: 0.85rem;
  color: #4a5568;
  position: relative;
  padding-left: 15px;
}

.machine-features li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #ff6b35;
  font-weight: bold;
}





/* Responsive Design */
@media (max-width: 1200px) {
  .machinery-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }
}

@media (max-width: 768px) {
  .machinery-section {
    padding: 40px 0;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .machinery-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .machine-info {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .machine-image-container {
    height: 180px;
  }
}
</style>